import { useState, useRef, useEffect, useMemo } from "react";
import { X, Dog, Cat, Calendar, Activity, ClipboardList, User, Phone, Syringe, Scissors, Heart, PawPrint, IndianRupee, Wallet } from "lucide-react";
import {
  FiChevronDown,
  FiCheckCircle,
  FiCamera,
  FiFileText,
  FiImage,
  FiCalendar,
} from "react-icons/fi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBone,
  faBriefcaseMedical,
  faPersonWalking,
  faPaw,
} from "@fortawesome/free-solid-svg-icons";
import { useProfile } from "../../context/ProfileContext";
import Appointments from "../client_pets/PetsAppointments";
import { httpService } from "../../services/httpService";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import PetHeader from "./PetHeader";

// Optional – keep these if you still need nested drawers
import SelectAddOnsDrawer from "./SelectAddOnsDrawer";
import PetServiceCategoryDrawer from "../Orders/PetServiceCategoryDrawer";
import AddAppointment from "../Calendar/BigCalendar/appointmentcalendar/AddAppointment";
import config from "../../config";

// S3 base URL
const S3_BASE_URL = (
  config.S3_BASE_URL || 
  process.env.REACT_APP_S3_BASE_URL ||
  "https://flps2025.s3.ap-south-1.amazonaws.com"
).replace(/\/+$/, "");

const resolvePreviewUrl = (preview) => {
  if (!preview) return "/icons/default-service.svg";
  if (preview.startsWith("blob:")) return preview;
  if (/^https?:\/\//i.test(preview)) return preview;
  return `${S3_BASE_URL}/${preview.replace(/^\/+/, "")}`;
};

// Activity type order for sorting
const ACTIVITY_TYPE_ORDER = {
  feeding: 1,
  walking: 2,
  medication: 3,
  custom: 4,
  activity: 5,
};

// Slot config
const SLOT_CONFIG = [
  { key: "Morning", label: "Morning" },
  { key: "Afternoon", label: "Afternoon" },
  { key: "Evening", label: "Evening" },
];

const getAllSlotKeys = (slots) => {
  if (!slots) return [];
  return Object.keys(slots);
};

const getActivitiesForSlot = (slots, slotKey) => {
  if (!slots) return [];
  const targetKey = slotKey.toLowerCase();
  return Object.keys(slots)
    .filter((key) => {
      const currentKey = key.toLowerCase();
      if (
        targetKey === "afternoon" &&
        (currentKey === "afternoon" || currentKey === "noon")
      ) {
        return true;
      }
      return currentKey === targetKey;
    })
    .flatMap((key) => slots[key] || []);
};

const sortActivities = (activities) => {
  return [...activities].sort((a, b) => {
    const typeA = a?.scheduled_task_type?.toLowerCase() || "custom";
    const typeB = b?.scheduled_task_type?.toLowerCase() || "custom";
    const orderA = ACTIVITY_TYPE_ORDER[typeA] || 99;
    const orderB = ACTIVITY_TYPE_ORDER[typeB] || 99;
    return orderA - orderB;
  });
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  if (
    typeof dateString === "string" &&
    dateString.includes("-") &&
    !dateString.includes("T")
  ) {
    const [year, month, day] = dateString.split("-");
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }
  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatDay = (dateString) => {
  if (!dateString) return "";
  if (
    typeof dateString === "string" &&
    dateString.includes("-") &&
    !dateString.includes("T")
  ) {
    const [year, month, day] = dateString.split("-");
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
    });
  }
  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
};

const getBoardingDuration = (start, end, fallback) => {
  if (fallback !== undefined && fallback !== null && fallback !== "") {
    if (typeof fallback === "string" && /day/i.test(fallback)) return fallback;
    return `${fallback} ${Number(fallback) === 1 ? "Day" : "Days"}`;
  }
  if (!start || !end) return "—";
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return "—";
  }
  const days = Math.floor(
    (new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()) -
      new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())) /
      86400000,
  ) + 1;
  return `${Math.max(days, 1)} ${days === 1 ? "Day" : "Days"}`;
};

const getBoardingActivityId = (boardingItem) =>
  boardingItem?.ref_id ||
  boardingItem?.boarding_info?.ref_id ||
  boardingItem?.item?.ref_id ||
  boardingItem?.booking_id ||
  boardingItem?._orderId ||
  boardingItem?._id;

// Helper to extract pet ID from a boarding item
const getPetIdFromBoardingItem = (item) => {
  if (!item) return null;
  const pet = item.pet;
  return (
    item.pet_id ||
    item.vendor_pet_id ||
    item.vendor_petId ||
    item.petId ||
    item._petId ||
    item.pet_details?._id ||
    item.pet_details?.id ||
    item.item?.pet_id ||
    item.item?.vendor_pet_id ||
    item.item?.pet_details?._id ||
    item.boarding_info?.pet_id ||
    item.boarding_info?.vendor_pet_id ||
    item.boarding_info?.pet_details?._id ||
    item.boarding_info?.pet_details?.id ||
    item.appointment_info?.pet_id ||
    item.appointment_info?.vendor_pet_id ||
    item.appointment_info?.pet_details?._id ||
    item.appointment_info?.pet_details?.id ||
    (typeof pet === "object" ? pet?._id || pet?.id : pet) ||
    null
  );
};

const getMoney = (...values) => {
  const value = values.find((candidate) => candidate !== undefined && candidate !== null && candidate !== "");
  return Number(value) || 0;
};

const isPetOrderItem = (item, petId) => {
  const itemPetId = getPetIdFromBoardingItem(item);
  return itemPetId && petId && String(itemPetId) === String(petId);
};

const getServiceTimeline = (orders, petId) => {
  const services = (orders || []).flatMap((order) => {
    const orderItems = order.items || order.order_items || order.services || [];
    return orderItems
      .filter((item) => isPetOrderItem(item, petId) || isPetOrderItem(order, petId))
      .map((item) => ({
        ...item,
        _orderId: order._id,
        order_number: order.order_number,
        createdAt: order.createdAt || order.created_at,
      }));
  });

  const uniqueServices = new Map();
  services.forEach((service) => {
    const key = service._id || service.ref_id || `${service._orderId || ""}_${service.type || service.item_type || service.service_name || ""}`;
    if (!uniqueServices.has(key)) uniqueServices.set(key, service);
  });

  return Array.from(uniqueServices.values()).sort((a, b) => {
    const dateA = new Date(a.appointment_info?.appointment_date || a.boarding_info?.start_date || a.createdAt || a.date || 0);
    const dateB = new Date(b.appointment_info?.appointment_date || b.boarding_info?.start_date || b.createdAt || b.date || 0);
    return dateB - dateA;
  });
};

const PetOverviewStats = ({ petId, boardingHistory, clientOverview }) => {
  const petOrders = (clientOverview?.order_details || []).flatMap((order) => {
    const orderItems = order.items || order.order_items || order.services || [];
    const matchingItems = orderItems.filter((item) => isPetOrderItem(item, petId));
    return matchingItems.length ? [{ order, items: matchingItems }] : [];
  });

  const stats = petOrders.reduce(
    (result, { order, items }) => {
      const orderTotal = getMoney(
        order.grand_total,
        order.amount_after_adjustment,
        order.total_amount,
      );
      const itemTotal = items.reduce(
        (sum, item) => sum + getMoney(item.final_total, item.total_amount, item.amount),
        0,
      );
      const spend = itemTotal || orderTotal;
      const paid = items.reduce(
        (sum, item) => sum + getMoney(item.total_paid_amount, item.paid_amount, item.paid),
        0,
      ) || getMoney(order.total_paid_amount);

      result.visits += 1;
      result.spend += spend;
      result.paid += paid;
      return result;
    },
    { visits: boardingHistory.length, spend: 0, paid: 0 },
  );

  const outstanding = Math.max(stats.spend - stats.paid, 0);
  const cards = [
    { label: "Total Visits", value: stats.visits, icon: PawPrint, className: "bg-blue-50 border-blue-200 text-blue-700" },
    { label: "Total Spend", value: `₹${stats.spend.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`, icon: IndianRupee, className: "bg-green-50 border-green-200 text-green-700" },
    { label: "Outstanding", value: `₹${outstanding.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`, icon: Wallet, className: "bg-amber-50 border-amber-200 text-amber-700" },
  ];

  return (
    <div className="p-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {cards.map(({ label, value, icon: Icon, className }) => (
          <div key={label} className={`rounded-lg border p-4 shadow-sm ${className}`}>
            <Icon className="w-5 h-5 mb-3" />
            <p className="text-sm font-medium text-slate-600">{label}</p>
            <p className="mt-1 text-xl font-bold text-slate-900">{value}</p>
          </div>
        ))}
      </div>
      {!petOrders.length && !boardingHistory.length && (
        <p className="mt-6 text-center text-sm text-slate-500">No visit or payment history found for this pet.</p>
      )}
    </div>
  );
};

const normalizePetDetailsList = (value) => {
  if (!value) return [];
  const values = Array.isArray(value) ? value : [value];
  return values
    .map((entry) => {
      if (typeof entry === "string" || typeof entry === "number") {
        return String(entry);
      }
      return entry?.name || entry?.label || entry?.value || entry?.condition || "";
    })
    .filter(Boolean);
};

const getActivityLabel = (activity) => {
  const type = activity?.scheduled_task_type?.toLowerCase();
  if (type === "feeding") {
    return activity?.raw_data?.whatToFeed || activity.description || "Feeding";
  }
  if (type === "walking" || type === "walk") {
    if (activity?.raw_data) {
      const duration = activity.raw_data.duration
        ? `${activity.raw_data.duration} min`
        : "";
      const notes = activity.raw_data.notes || "";
      return (
        [duration, notes].filter(Boolean).join(", ") ||
        activity.description ||
        "Walk"
      );
    }
    return activity.description || "Walk";
  }
  if (type === "medication") {
    return activity.description || "Medication";
  }
  return activity.description || "Custom activity";
};

const getActivityIcon = (activity) => {
  const type = activity?.scheduled_task_type?.toLowerCase();
  if (type === "feeding") {
    return (
      <FontAwesomeIcon icon={faBone} className="text-orange-500 shrink-0" />
    );
  }
  if (type === "walking" || type === "walk") {
    return (
      <FontAwesomeIcon
        icon={faPersonWalking}
        className="text-blue-500 shrink-0"
      />
    );
  }
  if (type === "medication") {
    return (
      <FontAwesomeIcon
        icon={faBriefcaseMedical}
        className="text-red-500 shrink-0"
      />
    );
  }
  return <FontAwesomeIcon icon={faPaw} className="text-indigo-500 shrink-0" />;
};

const getActivityTypeName = (activity) => {
  const type = activity?.scheduled_task_type?.toLowerCase();
  switch (type) {
    case "feeding":
      return "Feeding";
    case "walking":
    case "walk":
      return "Walk";
    case "medication":
      return "Medication";
    default:
      return activity?.description || "Custom Activity";
  }
};

const getActivityDetails = (activity) => {
  const type = activity?.scheduled_task_type?.toLowerCase();
  if (type === "feeding") {
    return activity?.raw_data?.whatToFeed || "";
  }
  if (type === "walking" || type === "walk") {
    if (activity?.raw_data) {
      const duration = activity.raw_data.duration
        ? `${activity.raw_data.duration} min`
        : "";
      const notes = activity.raw_data.notes || "";
      return [duration, notes].filter(Boolean).join(", ");
    }
    return "";
  }
  if (type === "medication") {
    return (
      activity?.raw_data?.medicine_name ||
      activity?.raw_data?.medicineName ||
      activity?.raw_data?.name ||
      ""
    );
  }
  return "";
};

const BoardingPetDetailsDrawer = ({
  petData,
  isOpen,
  onClose,
  onRefresh,
  clientData = {},
  boardingData = null, // current boarding record (if opened after check-in)
  isPostCheckIn = false, // flag: true when opened after check-in
  triggergetclientapi = false,
  clientBoarding = [], // boarding appointments passed from parent (to show all)
  clientAppointments = [],
}) => {
  const {
    name = "",
    age,
    breed,
    pet_type,
    weight,
    weight_kg,
    pet_weight,
    body_weight,
    wt,
    gender,
    tags = [],
    date_of_birth,
    birthday,
    color,
    coat,
    microchip_id,
    registration_number,
    vaccination_status,
    last_vet_visit,
    medical_notes,
    medical_conditions = [],
    behavior_notes,
    neutered_spayed,
    vaccinated_for = [],
  } = petData || {};

  const { isPlanExpired } = useProfile();

  const [isLoading, setIsLoading] = useState(false);
  const [petDetailsData, setPetDetailsData] = useState(null);
  const [clientOverview, setClientOverview] = useState(null);
  const [serviceTimeline, setServiceTimeline] = useState([]);
  const [boardingHistory, setBoardingHistory] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [activitiesByOrder, setActivitiesByOrder] = useState({}); // Store activities by order ID
  const [timeline, setTimeline] = useState([]);
  const [checkInActivities, setCheckInActivities] = useState(null);
  const [expandedAccordion, setExpandedAccordion] = useState(0); // First accordion open by default
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [expandedSubTab, setExpandedSubTab] = useState({}); // for sub-tabs inside each boarding item

  // Try multiple weight field names
  const finalWeight = weight_kg || weight || pet_weight || body_weight || wt;

  const [petImage, setPetImage] = useState(petData?.image || null);
  const [activeTab, setActiveTab] = useState("boarding_history"); // "boarding_history", "overview", "timeline"
  const fileInputRef = useRef(null);
  const drawerRef = useRef(null);

  // Nested drawer states (keep if you still use them)
  const [isAddonsDrawerOpen, setIsAddonsDrawerOpen] = useState(false);
  const [selectedServiceForAddons, setSelectedServiceForAddons] = useState(null);
  const [showPetServiceDrawer, setShowPetServiceDrawer] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [showAddAppointmentDrawer, setShowAddAppointmentDrawer] = useState(false);
  const [appointmentClientDetails, setAppointmentClientDetails] = useState(null);

  const petType = pet_type?.name?.toLowerCase() || "pet";
  const breedName = breed?.breed_name || breed || "Unknown breed";
  const formattedAge = age ? `${age} ${age === 1 ? "" : ""}` : "";
  const formattedGender = gender
    ? gender.charAt(0).toUpperCase() + gender.slice(1)
    : "";
  const formattedWeight = finalWeight ? `${finalWeight} kg` : "";
  const isNeutered =
    neutered_spayed === true ||
    neutered_spayed === "yes" ||
    neutered_spayed === "Neutered" ||
    neutered_spayed === "Spayed";

  const headerPet = petDetailsData || petData || {};
  const headerSource = headerPet.data || headerPet;
  const headerName = headerSource.name || name || "Unknown Pet";
  const headerBreed =
    headerSource.breed?.breed_name ||
    headerSource.breed_info?.breed_name ||
    headerSource.breed_name ||
    breedName;
  const headerPetType =
    headerSource.pet_type?.name ||
    headerSource.pet_type_info?.name ||
    headerSource.pet_type_name ||
    petType;
  const headerGender = headerSource.gender || formattedGender;
  const headerAge = headerSource.age ?? headerSource.pet_age ?? formattedAge;
  const headerWeight =
    headerSource.weight_kg ??
    headerSource.weight ??
    headerSource.pet_weight ??
    headerSource.body_weight ??
    headerSource.wt ??
    finalWeight;
  const headerNeuteredStatus =
    headerSource.neutered_spayed ??
    headerSource.neutered_status ??
    (isNeutered ? "Neutered" : "Unknown");
  const headerMedicalConditions = normalizePetDetailsList(
    headerSource.medical_conditions ||
      headerSource.medical_condition ||
      headerSource.medicalConditions ||
      headerSource.medicalCondition ||
      headerSource.medical_notes,
  );
  const headerVaccines = normalizePetDetailsList(
    headerSource.vaccinated_for ||
      headerSource.vaccinations ||
      headerSource.vaccines ||
      headerSource.vaccine_details ||
      headerSource.vaccination_status ||
      headerSource.vaccinationStatus,
  );
  const headerVaccineRecords = (
    Array.isArray(headerSource.vaccination_records)
      ? headerSource.vaccination_records
      : headerVaccines.map((vaccine) => ({ vaccine }))
  )
    .map((record) => ({
      name:
          typeof record === "string"
            ? record
            : record?.vaccine ||
              record?.name ||
              record?.label ||
              record?.vaccination ||
              "Vaccine",
      dateGiven: record?.date_given || record?.dateGiven || record?.given_on,
      nextDue: record?.next_due || record?.nextDue || record?.due_date,
    }))
    .filter((record) => record.name);
  const headerIsNeutered =
    headerPet.neutered_spayed === true ||
    ["yes", "neutered", "spayed"].includes(
      String(headerPet.neutered_spayed || "").toLowerCase(),
    );
  const headerCoat =
    headerSource.coat ||
    headerSource.breed?.breed_coat ||
    headerSource.breed_info?.breed_coat ||
    coat ||
    "—";
  const headerTags =
    headerSource.tags ||
    headerSource.pet_tags ||
    tags ||
    [];
  const headerColor =
    headerSource.color ||
    headerSource.colors ||
    headerSource.pet_color ||
    color ||
    [];
  const headerDiet =
    headerSource.meal_type ||
    headerSource.mealType ||
    headerSource.diet ||
    headerSource.what_to_feed ||
    [];
  const headerMealsPerDay =
    headerSource.meals_per_day ||
    headerSource.mealsPerDay ||
    null;
  const headerSize =
    headerSource.size?.size_label ||
    headerSource.size?.name ||
    headerSource.size ||
    "";

  // Owner info
  const ownerName =
    `${clientData?.firstname || ""} ${clientData?.lastname || ""}`.trim() ||
    "—";
  const ownerMobile = clientData?.phone || "—";

  // Fetch boarding-specific data
  useEffect(() => {
    if (!isOpen || !petData || !petData._id) return;

    const fetchBoardingData = async () => {
      setIsLoading(true);
      try {
        // 1. Fetch the shared overview (old API) when requested
        let orders = [];
        if (triggergetclientapi && petData.vendor_client_id) {
          const response = await httpService.get(
            `/vendorclient/getVendorClientPetsOverviewById/${petData.vendor_client_id}`
          );
          const overview = response?.data?.data || response?.data || {};
          setClientOverview(overview);
          orders =
            overview.order_details ||
            overview.orders ||
            response?.data?.order_details ||
            response?.data?.orders ||
            [];
          setServiceTimeline(getServiceTimeline(orders, petData._id || petData.id));
          // keep pet details if available
          const foundPet = response?.data?.data?.pet_details?.find(
            (p) => String(p._id || p.id) === String(petData._id || petData.id),
          );
            const headerPet = petDetailsData || petData || {};
            const headerMedicalConditions = normalizePetDetailsList(
              headerPet.medical_conditions ||
                headerPet.medical_condition ||
                headerPet.medical_notes,
            );
            const headerVaccines = normalizePetDetailsList(
              headerPet.vaccinated_for ||
                headerPet.vaccines ||
                headerPet.vaccine_details ||
                headerPet.vaccination_status,
            );
            const headerIsNeutered =
              headerPet.neutered_spayed === true ||
              ["yes", "neutered", "spayed"].includes(
                String(headerPet.neutered_spayed || "").toLowerCase(),
              );
          setPetDetailsData(foundPet || petData);
        } else {
          setPetDetailsData(petData);
          setClientOverview(null);
          setServiceTimeline([]);
        }

        // 2. From orders, extract only boarding-related items
        const lowerIncludesBoard = (str) =>
          typeof str === "string" && str.toLowerCase().includes("board");

        const boardingFromOrders = [];
        orders.forEach((order) => {
          const orderItems = order.items || order.order_items || order.services || [];
          orderItems.forEach((item) => {
            const t1 = item.item_type || item.type || item.service_type_name || item.service_name || "";
            const matchesBoarding =
              lowerIncludesBoard(item.item_type) ||
              lowerIncludesBoard(item.type) ||
              lowerIncludesBoard(item.service_type_name) ||
              lowerIncludesBoard(item.service_name) ||
              lowerIncludesBoard(item.details) ||
              lowerIncludesBoard(item.item?.service_name) ||
              lowerIncludesBoard(item.item?.item_type) ||
              false;

            if (matchesBoarding) {
              boardingFromOrders.push({
                // mark as order item so renderer can detect
                ...item,
                _petId:
                  getPetIdFromBoardingItem(item) ||
                  getPetIdFromBoardingItem(order),
                _orderId: order._id,
                order_number: order.order_number,
                createdAt: order.createdAt || order.created_at,
                isOrderItem: true,
              });
            }
          });
        });

        // 3. Merge with clientBoarding prop (parent may pass raw boarding records)
        const rawBoarding = [...(clientBoarding || [])];

        // Combine and dedupe by _id (prefer entry._id), otherwise ref_id
        const combined = [...boardingFromOrders, ...rawBoarding];
        const map = new Map();
        combined.forEach((it) => {
          const key = it._id || it.ref_id || `${it._orderId || ""}_${it.item_id || it._id || JSON.stringify(it)}`;
          if (!map.has(key)) map.set(key, it);
        });

        const merged = Array.from(map.values());

        // 🟢 FILTER BY CURRENT PET ID
        const petId = petData._id || petData.id;
        const filteredByPet = merged.filter((item) => {
          const itemPetId = getPetIdFromBoardingItem(item);
          if (!itemPetId) return false;
          return String(itemPetId) === String(petId);
        });

        // 4. Sort newest first by available date fields
        const getSortDate = (it) => {
          const date =
            it.check_in_date ||
            it.start_date ||
            it.appointment_date ||
            it.createdAt ||
            it.date ||
            it.created_at ||
            null;
          return date ? new Date(date).getTime() : 0;
        };

        filteredByPet.sort((a, b) => getSortDate(b) - getSortDate(a));

        setBoardingHistory(filteredByPet || []);

        // NOTE: intentionally not calling /pets/timeline or /boarding/checkin-activities per requirement #4
      } catch (error) {
        console.error("Failed to fetch boarding pet details:", error);
        setBoardingHistory([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBoardingData();
  }, [
    isOpen,
    petData && petData._id,
    petData && petData.vendor_client_id,
    triggergetclientapi,
    clientBoarding?.length,
  ]);

  // Fetch boarding activities/schedule
  useEffect(() => {
    if (!isOpen || !boardingData?.ref_id) return;

    const fetchActivities = async () => {
      try {
        const response = await httpService.get(
          `/serviceactivity/boarding/${boardingData.ref_id}/activities`,
        );
        const scheduleData =
          response?.data?.data?.schedule ||
          response?.data?.schedule ||
          response?.schedule ||
          [];
        setSchedule(scheduleData);
      } catch (err) {
        console.error("Failed to fetch boarding activities:", err);
        setSchedule([]);
      }
    };

    fetchActivities();
  }, [isOpen, boardingData?.ref_id]);

  // Fetch activities for a specific boarding order
  const fetchOrderActivities = async (orderId) => {
    if (activitiesByOrder[orderId]) return; // Already cached
    
    try {
      const response = await httpService.get(
        `/serviceactivity/boarding/${orderId}/activities`,
      );
      const scheduleData =
        response?.data?.data?.schedule ||
        response?.data?.schedule ||
        response?.schedule ||
        [];
      const normalizedSchedule = Array.isArray(scheduleData)
        ? scheduleData
        : scheduleData?.days || scheduleData?.schedule || [];
      setActivitiesByOrder((prev) => ({
        ...prev,
        [orderId]: normalizedSchedule,
      }));
    } catch (err) {
      console.error(`Failed to fetch activities for order ${orderId}:`, err);
      setActivitiesByOrder((prev) => ({
        ...prev,
        [orderId]: [],
      }));
    }
  };

  useEffect(() => {
    if (!isOpen || expandedAccordion < 0) return;

    const boardingItem = boardingHistory[expandedAccordion];
    const orderId = getBoardingActivityId(boardingItem);
    if (orderId && !activitiesByOrder[orderId]) {
      fetchOrderActivities(orderId);
    }
  }, [isOpen, expandedAccordion, boardingHistory, activitiesByOrder]);

  // Handle drawer close with refresh
  const handleClose = () => {
    onClose();
    if (onRefresh) onRefresh();
  };

  // Click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isPortalClick =
        event.target.closest("[data-portal]") ||
        event.target.closest(".dropdown-menu") ||
        event.target.parentNode === document.body ||
        event.target.parentNode?.parentNode === document.body;

      const isBackdropClick =
        event.target.classList.contains("fixed") &&
        event.target.classList.contains("inset-0") &&
        event.target.classList.contains("z-[9998]");

      if (
        drawerRef.current &&
        !drawerRef.current.contains(event.target) &&
        !event.target.closest(".pet-service-drawer") &&
        !isAddonsDrawerOpen &&
        !showPetServiceDrawer &&
        !showAddAppointmentDrawer &&
        !isPortalClick &&
        !isBackdropClick
      ) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, isAddonsDrawerOpen, showPetServiceDrawer, showAddAppointmentDrawer]);

  useEffect(() => {
    if (!isOpen) {
      setPetDetailsData(null);
      setBoardingHistory([]);
      setTimeline([]);
      setCheckInActivities(null);
      setSchedule([]);
      setActivitiesByOrder({});
      setSelectedPhoto(null);
      setActiveTab("boarding_history");
      setExpandedSubTab({});
    }
  }, [isOpen]);

  const getPetIcon = () => {
    if (petType.includes("cat")) {
      return <Cat className="w-12 h-12 text-gray-600" strokeWidth={1.5} />;
    }
    return <Dog className="w-12 h-12 text-gray-600" strokeWidth={1.5} />;
  };

  const handleImageClick = () => fileInputRef.current?.click();
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setPetImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const image =
      petDetailsData?.image ||
      petDetailsData?.pet_image ||
      petDetailsData?.pet_images?.[0] ||
      petData?.image ||
      petData?.pet_image ||
      petData?.pet_images?.[0] ||
      null;
    setPetImage(image);
  }, [petDetailsData, petData]);

  // ---------- Tabs ----------
  const tabs = [
    { id: "boarding_history", label: "Boarding History", icon: Calendar },
    { id: "overview", label: "Overview", icon: ClipboardList },
    { id: "timeline", label: "Timeline", icon: Activity },
  ];

  // ---------- Render helpers ----------
  const InfoItem = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-2">
      {Icon && <Icon className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />}
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-medium text-gray-900">{value || "—"}</p>
      </div>
    </div>
  );

  // Calculate summary data from schedule
  const summary = useMemo(() => {
    const allActivities = schedule.flatMap((day) => {
      const slots = day?.slots || {};
      const slotKeys = getAllSlotKeys(slots);
      return slotKeys.flatMap((slotKey) => {
        const activitiesInSlot = slots[slotKey] || [];
        return activitiesInSlot.map((act) => ({
          ...act,
          displayDate: day.date,
          displaySlot: slotKey,
        }));
      });
    });

    const completed = allActivities.filter(
      (a) => a?.is_completed === true,
    ).length;

    const photosCount = allActivities.reduce(
      (total, a) => total + (a?.photos?.length || 0),
      0,
    );

    const medications = allActivities.filter(
      (a) => a?.scheduled_task_type?.toLowerCase() === "medication",
    );
    const completedMedications = medications.filter(
      (a) => a?.is_completed === true,
    ).length;

    const notesList = allActivities
      .filter((a) => a.notes && a.notes.trim() !== "")
      .sort((a, b) => {
        const dateA = new Date(a.displayDate);
        const dateB = new Date(b.displayDate);
        if (dateA - dateB !== 0) return dateA - dateB;
        const typeA = a?.scheduled_task_type?.toLowerCase() || "custom";
        const typeB = b?.scheduled_task_type?.toLowerCase() || "custom";
        const orderA = ACTIVITY_TYPE_ORDER[typeA] || 99;
        const orderB = ACTIVITY_TYPE_ORDER[typeB] || 99;
        return orderA - orderB;
      });

    const photosList = allActivities.flatMap((a) => {
      if (!a.photos || !Array.isArray(a.photos)) return [];
      return a.photos.map((url) => ({
        url,
        date: a.displayDate,
        slot: a.displaySlot,
        activity: a,
        activityTitle: getActivityTypeName(a),
        activityDetails: getActivityDetails(a),
        notes: a.notes,
      }));
    });

    return {
      total: allActivities.length,
      completed,
      photos: photosCount,
      medications: medications.length,
      completedMedications,
      notesList,
      photosList,
    };
  }, [schedule]);

  const renderOverview = () => (
    <PetOverviewStats
      petId={petData?._id || petData?.id}
      boardingHistory={boardingHistory}
      clientOverview={clientOverview}
    />
  );

  // ---------- Helper to render sub-tab content for a single boarding item ----------
  const renderSubTabContent = (orderActivities, subTab) => {
    if (subTab === "schedule") {
      if (!orderActivities || orderActivities.length === 0) {
        return (
          <div className="px-5 py-8 text-center text-slate-500 bg-slate-50/50 text-sm">
            No activities scheduled for this boarding stay.
          </div>
        );
      }

      // Group by date
      const groupedByDate = orderActivities.reduce((acc, act) => {
        const date = act.displayDate;
        if (!acc[date]) acc[date] = [];
        acc[date].push(act);
        return acc;
      }, {});

      const sortedDates = Object.keys(groupedByDate).sort();

      return (
        <div className="overflow-x-auto">
          <div
            className="grid border-b border-slate-200 bg-slate-50 min-w-[500px]"
            style={{
              gridTemplateColumns: `100px repeat(${SLOT_CONFIG.length}, 1fr)`,
            }}
          >
            <div className="px-4 py-3 text-sm font-semibold text-slate-600 border-r border-slate-200">
              Date
            </div>
            {SLOT_CONFIG.map((slot) => (
              <div
                key={slot.key}
                className="px-4 py-3 text-sm font-semibold text-slate-700 border-r last:border-r-0 border-slate-200 text-center"
              >
                {slot.label}
              </div>
            ))}
          </div>
          {sortedDates.map((date) => {
            const dayActivities = groupedByDate[date];
            // For each slot, filter activities
            const slotMap = {};
            SLOT_CONFIG.forEach((slot) => {
              const acts = getActivitiesForSlot(
                { [slot.key]: dayActivities.filter(a => a.displaySlot?.toLowerCase() === slot.key.toLowerCase()) },
                slot.key
              );
              slotMap[slot.key] = sortActivities(acts);
            });

            return (
              <div
                key={date}
                className="grid border-b last:border-b-0 border-slate-200 min-w-[500px] hover:bg-slate-50/30 transition-colors"
                style={{
                  gridTemplateColumns: `100px repeat(${SLOT_CONFIG.length}, 1fr)`,
                }}
              >
                <div className="px-4 py-4 text-sm font-bold text-slate-900 whitespace-nowrap border-r border-slate-200 bg-slate-50/50">
                  {formatDay(date)}
                </div>
                {SLOT_CONFIG.map((slot) => {
                  const activities = slotMap[slot.key] || [];
                  return (
                    <div
                      key={slot.key}
                      className="px-4 py-4 min-w-0 border-r last:border-r-0 border-slate-200"
                    >
                      {activities.length === 0 ? (
                        <span className="text-sm text-slate-300">—</span>
                      ) : (
                        <div className="space-y-3">
                          {activities.map((activity, idx) => (
                            <div key={activity._id || idx} className="flex items-center gap-2">
                              <div className="flex items-center justify-center w-5 h-5 shrink-0">
                                {activity.is_completed ? (
                                  <FiCheckCircle className="w-4 h-4 text-green-700" />
                                ) : (
                                  <span className="w-5 h-5 rounded-full border-2 border-slate-300" />
                                )}
                              </div>
                              <div className="flex items-center justify-center w-5 h-5 shrink-0">
                                {getActivityIcon(activity)}
                              </div>
                              <span className="text-sm text-slate-600 truncate">
                                {getActivityLabel(activity)}
                              </span>
                              {/* {(activity.notes || activity.raw_data?.notes) && (
                                // <FiFileText
                                //   className="w-3.5 h-3.5 text-blue-500 shrink-0 cursor-help"
                                //   title={activity.notes || activity.raw_data.notes}
                                // />
                              )} */}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      );
    }

    if (subTab === "notes") {
      const notesList = orderActivities
        .filter((a) => a.notes && a.notes.trim() !== "")
        .sort((a, b) => new Date(a.displayDate) - new Date(b.displayDate));

      if (notesList.length === 0) {
        return (
          <div className="px-5 py-8 text-center text-slate-500 bg-slate-50/50 text-sm">
            No notes logged for this stay.
          </div>
        );
      }

      return (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-[120px]">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-[140px]">Activity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider w-[120px]">Slot</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {notesList.map((activity, idx) => {
                const prev = idx > 0 ? notesList[idx - 1] : null;
                const showDate = !prev || prev.displayDate !== activity.displayDate;
                const capitalizedSlot = activity.displaySlot
                  ? activity.displaySlot.charAt(0).toUpperCase() + activity.displaySlot.slice(1).toLowerCase()
                  : "";
                return (
                  <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-slate-700">
                      {showDate ? formatDay(activity.displayDate) : ""}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">
                      <div className="flex items-center gap-2">
                        {getActivityIcon(activity)}
                        <span>{getActivityTypeName(activity)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">
                      <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                        {capitalizedSlot}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 break-words">
                      {activity.notes}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    if (subTab === "photos") {
      const photosList = orderActivities.flatMap((a) => {
        if (!a.photos || !Array.isArray(a.photos)) return [];
        return a.photos.map((url) => ({
          url,
          date: a.displayDate,
          slot: a.displaySlot,
          activity: a,
          activityTitle: getActivityTypeName(a),
          activityDetails: getActivityDetails(a),
          notes: a.notes,
        }));
      });

      if (photosList.length === 0) {
        return (
          <div className="px-5 py-8 text-center text-slate-500 bg-slate-50/50 text-sm">
            No photos uploaded for this stay.
          </div>
        );
      }

      return (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {photosList.map((photo, idx) => {
            const imageUrl = resolvePreviewUrl(photo.url);
            return (
              <div
                key={idx}
                className="relative group rounded-lg overflow-hidden border border-slate-200 aspect-square bg-slate-100 cursor-pointer hover:shadow-lg transition"
                onClick={() => setSelectedPhoto(photo)}
              >
                <img
                  src={imageUrl}
                  alt={photo.activityTitle || "Activity"}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/icons/default-service.svg";
                  }}
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/90 to-transparent p-2 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                  <p className="text-white text-xs font-bold line-clamp-1">{photo.activityTitle}</p>
                  {photo.activityDetails && <p className="text-slate-300 text-[10px]">{photo.activityDetails}</p>}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return null;
  };

  // ---------- RENDER BOARDING HISTORY WITH SUB-TABS ----------
  const renderBoardingHistory = () => {
    if (!boardingHistory || boardingHistory.length === 0) {
      return (
        <div className="p-5 text-center py-12 text-gray-500">
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No boarding history found</p>
        </div>
      );
    }

    return (
      <div className="p-5 space-y-3">
        {boardingHistory.map((raw, idx) => {
          const item = raw.item || raw;
          const start =
            item.check_in_date ||
            item.start_date ||
            item.appointment_date ||
            item.boarding_info?.start_date ||
            item.appointment_info?.appointment_date ||
            item.createdAt ||
            item.date ||
            item.created_at ||
            null;
          const end =
            item.check_out_date ||
            item.end_date ||
            item.checkout_date ||
            item.boarding_info?.end_date ||
            item.end_date ||
            null;
          const serviceName =
            item.service_name ||
            item.serviceTypeName ||
            item.details ||
            item.boarding_info?.boarding_services?.name ||
            item.appointment_info?.service_name ||
            "Boarding";
          const status =
            item.status ||
            item.boarding_info?.status ||
            item.appointment_info?.status ||
            "—";
          const notes =
            item.notes ||
            item.special_instructions ||
            item.boarding_info?.notes ||
            item.boarding_info?.special_instructions ||
            null;
          const orderNo =
            raw.order_number || raw._orderId || null;
          const isOpen = idx === expandedAccordion;

          // Unique key for this boarding item
          const itemKey = raw._id || raw.ref_id || `item-${idx}`;

          // Get activities for this specific boarding order
          const orderId = getBoardingActivityId(raw);
          const orderSchedule = activitiesByOrder[orderId] || [];
          
          const orderActivities = orderSchedule.flatMap((day) => {
            const slots = day?.slots || {};
            const slotKeys = getAllSlotKeys(slots);
            return slotKeys.flatMap((slotKey) => {
              const activitiesInSlot = slots[slotKey] || [];
              return activitiesInSlot.map((act) => ({
                ...act,
                displayDate: day.date,
                displaySlot: slotKey,
                displayNotes: act.notes || act.raw_data?.notes || "",
              }));
            });
          });

          // Determine active sub-tab for this item (default to "schedule")
          const activeSubTab = expandedSubTab[itemKey] || "schedule";

          // Counts for badges
          const notesCount = orderActivities.filter(a => a.notes && a.notes.trim() !== "").length;
          const photosCount = orderActivities.reduce((total, a) => total + (a.photos?.length || 0), 0);

          return (
            <div
              key={itemKey}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              <button
                onClick={() => {
                  const newIdx = isOpen ? -1 : idx;
                  setExpandedAccordion(newIdx);
                  // Fetch activities for this order when expanding
                  if (newIdx === idx && orderId) {
                    fetchOrderActivities(orderId);
                  }
                }}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="text-left flex-1 min-w-0">
                   {/* {orderNo && (
                    <p className="font-semibold text-gray-500 mt-0.5">
                      Order: {orderNo}
                    </p>
                  )} */}
                   <p className="font-semibold text-gray-900 mt-0.5">
                    Check In: {start ? formatDate(start) : "—"} | Check Out: {end ? formatDate(end) : "Ongoing"} |
                    <span className="ml-3">
                      Duration: {getBoardingDuration(
                        start,
                        end,
                        item.duration || item.boarding_info?.duration,
                      )}
                    </span>
                  </p>
                  <p className="font-semibold text-gray-600 truncate">{orderNo}- {serviceName}</p>
                 
                 
                </div>
                <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                  <span
                    className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                      String(status).toLowerCase().includes("complete") ||
                      String(status).toLowerCase().includes("closed")
                        ? "bg-green-100 text-green-800"
                        : String(status).toLowerCase().includes("check") ||
                            String(status).toLowerCase().includes("start")
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {status || "—"}
                  </span>
                  <FiChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </div>
              </button>

              {isOpen && (
                <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 space-y-4">
                  {orderId && !Object.prototype.hasOwnProperty.call(activitiesByOrder, orderId) && (
                    <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm text-gray-500">
                      Loading boarding details...
                    </div>
                  )}

                  {/* SUB-TABS: Schedule | Notes | Photos */}
                  <div className="flex space-x-1 bg-slate-200/50 p-1 rounded-xl w-max">
                    <button
                      onClick={() => setExpandedSubTab(prev => ({ ...prev, [itemKey]: "schedule" }))}
                      className={`flex items-center gap-2 px-4 py-1.5 text-sm font-semibold rounded-lg transition-all ${
                        activeSubTab === "schedule"
                          ? "bg-white text-indigo-700 shadow-sm"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                      }`}
                    >
                      <FiCalendar className="w-4 h-4" />
                      Daily Schedule
                    </button>
                    <button
                      onClick={() => setExpandedSubTab(prev => ({ ...prev, [itemKey]: "notes" }))}
                      className={`flex items-center gap-2 px-4 py-1.5 text-sm font-semibold rounded-lg transition-all ${
                        activeSubTab === "notes"
                          ? "bg-white text-indigo-700 shadow-sm"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                      }`}
                    >
                      <FiFileText className="w-4 h-4" />
                      Notes
                      {notesCount > 0 && (
                        <span className="ml-1 bg-indigo-100 text-indigo-700 py-0.5 px-2 rounded-full text-xs">
                          {notesCount}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setExpandedSubTab(prev => ({ ...prev, [itemKey]: "photos" }))}
                      className={`flex items-center gap-2 px-4 py-1.5 text-sm font-semibold rounded-lg transition-all ${
                        activeSubTab === "photos"
                          ? "bg-white text-indigo-700 shadow-sm"
                          : "text-slate-600 hover:text-slate-900 hover:bg-slate-200"
                      }`}
                    >
                      <FiImage className="w-4 h-4" />
                      Photos
                      {photosCount > 0 && (
                        <span className="ml-1 bg-indigo-100 text-indigo-700 py-0.5 px-2 rounded-full text-xs">
                          {photosCount}
                        </span>
                      )}
                    </button>
                  </div>

                  {/* SUB-TAB CONTENT */}
                  <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                    {renderSubTabContent(orderActivities, activeSubTab)}
                  </div>

                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderTimeline = () => (
    <div className="p-5">
      {timeline.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Activity className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No timeline events found</p>
        </div>
      ) : (
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
          <div className="space-y-6">
            {timeline.map((event, idx) => (
              <div key={event._id || idx} className="relative pl-10">
                <div className="absolute left-2.5 w-3 h-3 rounded-full bg-blue-600 border-2 border-white shadow" />
                <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                  <div className="flex justify-between items-start">
                    <p className="font-medium text-gray-900 text-sm">
                      {event.title || event.event_type}
                    </p>
                    <span className="text-xs text-gray-500">
                      {event.date
                        ? new Date(event.date).toLocaleString()
                        : ""}
                    </span>
                  </div>
                  {event.description && (
                    <p className="text-sm text-gray-600 mt-1">
                      {event.description}
                    </p>
                  )}
                  {event.performed_by && (
                    <p className="text-xs text-gray-400 mt-1">
                      by {event.performed_by}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderSchedule = () => {
    if (schedule.length === 0) {
      return (
        <div className="p-5 text-center py-12 text-gray-500">
          No activities scheduled for this boarding stay.
        </div>
      );
    }

    return (
      <div className="p-5">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div
            className="grid border-b border-gray-200 bg-gray-50 min-w-[600px]"
            style={{
              gridTemplateColumns: `140px repeat(${SLOT_CONFIG.length}, 1fr)`,
            }}
          >
            <div className="px-4 py-3 text-sm font-semibold text-gray-600 border-r border-gray-200">
              Date
            </div>
            {SLOT_CONFIG.map((slot) => (
              <div
                key={slot.key}
                className="px-4 py-3 text-sm font-semibold text-gray-700 border-r last:border-r-0 border-gray-200 text-center"
              >
                {slot.label}
              </div>
            ))}
          </div>
          <div className="overflow-x-auto">
            {schedule.map((day) => (
              <div
                key={day.date}
                className="grid border-b last:border-b-0 border-gray-200 min-w-[600px] hover:bg-gray-50/30"
                style={{
                  gridTemplateColumns: `140px repeat(${SLOT_CONFIG.length}, 1fr)`,
                }}
              >
                <div className="px-4 py-3 text-sm font-bold text-gray-900 whitespace-nowrap border-r border-gray-200 bg-gray-50/50">
                  {formatDay(day.date)}
                </div>
                {SLOT_CONFIG.map((slot) => {
                  const activities = sortActivities(
                    getActivitiesForSlot(day?.slots || {}, slot.key),
                  );
                  return (
                    <div
                      key={slot.key}
                      className="px-4 py-3 min-w-0 border-r last:border-r-0 border-gray-200"
                    >
                      {activities.length === 0 ? (
                        <span className="text-sm text-gray-300">—</span>
                      ) : (
                        <div className="space-y-2">
                          {activities.map((activity) => {
                            const completed =
                              activity?.is_completed === true;
                            const label = getActivityLabel(activity);
                            return (
                              <div
                                key={activity._id}
                                className="flex items-center gap-2 text-sm"
                              >
                                <div className="flex items-center justify-center w-4 h-4 shrink-0">
                                  {completed ? (
                                    <FiCheckCircle className="w-4 h-4 text-green-700" />
                                  ) : (
                                    <span className="w-4 h-4 rounded-full border border-gray-300" />
                                  )}
                                </div>
                                <div className="flex items-center justify-center w-4 h-4 shrink-0">
                                  {getActivityIcon(activity)}
                                </div>
                                <span className="text-gray-600">{label}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderNotes = () => {
    if (summary.notesList.length === 0) {
      return (
        <div className="p-5 text-center py-12 text-gray-500">
          <FiFileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p>No notes have been logged for this boarding stay.</p>
        </div>
      );
    }

    return (
      <div className="p-5">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Activity
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Slot
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Note
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {summary.notesList.map((activity, idx) => {
                  const prevActivity =
                    idx > 0 ? summary.notesList[idx - 1] : null;
                  const showDate =
                    !prevActivity ||
                    prevActivity.displayDate !== activity.displayDate;
                  const capitalizedSlot = activity.displaySlot
                    ? activity.displaySlot.charAt(0).toUpperCase() +
                      activity.displaySlot.slice(1).toLowerCase()
                    : "";

                  return (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-700">
                        {showDate ? formatDay(activity.displayDate) : ""}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                          {getActivityIcon(activity)}
                          <span>{getActivityTypeName(activity)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                          {capitalizedSlot}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 max-w-xs">
                        <div className="whitespace-normal break-words">
                          {activity.notes}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderPhotos = () => {
    if (summary.photosList.length === 0) {
      return (
        <div className="p-5 text-center py-12 text-gray-500">
          <FiCamera className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p>No photos have been uploaded for this boarding stay.</p>
        </div>
      );
    }

    return (
      <div className="p-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {summary.photosList.map((photo, idx) => {
            const imageUrl = resolvePreviewUrl(photo.url);
            return (
              <div
                key={idx}
                className="relative group rounded-lg overflow-hidden border border-gray-200 aspect-square bg-gray-100 cursor-pointer hover:shadow-lg transition"
                onClick={() => setSelectedPhoto(photo)}
              >
                <img
                  src={imageUrl}
                  alt={photo.activityTitle || "Activity"}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/icons/default-service.svg";
                  }}
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-gray-900/90 to-transparent p-2 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                  <p className="text-white text-xs font-bold line-clamp-1">
                    {photo.activityTitle}
                  </p>
                  {photo.activityDetails && (
                    <p className="text-gray-300 text-[10px] mt-0.5">
                      {photo.activityDetails}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {selectedPhoto && (
          <div
            className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <div className="relative max-w-4xl w-full max-h-[90vh] bg-white rounded-lg overflow-hidden">
              <button
                onClick={() => setSelectedPhoto(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-black/70 flex items-center justify-center"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={resolvePreviewUrl(selectedPhoto.url)}
                alt={selectedPhoto.activityTitle}
                className="w-full h-auto max-h-[70vh] object-contain"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/icons/default-service.svg";
                }}
              />
              <div className="p-4 bg-white border-t border-gray-200">
                <p className="font-semibold text-gray-900">
                  {selectedPhoto.activityTitle}
                </p>
                {selectedPhoto.activityDetails && (
                  <p className="text-sm text-gray-600">
                    {selectedPhoto.activityDetails}
                  </p>
                )}
                <p className="text-sm text-gray-600 mt-1">
                  {formatDate(selectedPhoto.date)} • {selectedPhoto.slot}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderServiceTimeline = () => {
    if (serviceTimeline.length === 0) {
      return (
        <div className="p-5 text-center py-12 text-gray-500">
          <Activity className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No service history found</p>
        </div>
      );
    }

    return (
      <div className="p-5 space-y-3">
        {serviceTimeline.map((service, index) => {
          const appointmentInfo = service.appointment_info || {};
          const boardingInfo = service.boarding_info || {};
          const serviceType = service.type || service.item_type || service.service_type_name || "service";
          const typeLabel = String(serviceType).replace(/_/g, " ");
          const serviceName =
            service.service_name ||
            appointmentInfo.grooming_services?.name ||
            appointmentInfo.vetclinic_services?.name ||
            boardingInfo.boarding_services?.name ||
            service.name ||
            typeLabel;
          const serviceDate =
            appointmentInfo.appointment_date ||
            boardingInfo.start_date ||
            service.date ||
            service.createdAt;
          const endDate = boardingInfo.end_date || service.end_date;
          const status = service.status || appointmentInfo.status || boardingInfo.status || "Pending";
          const statusText = String(status).toLowerCase();
          const statusClassName =
            statusText.includes("complete") || statusText.includes("closed")
              ? "bg-green-100 text-green-800"
              : statusText.includes("no show") || statusText.includes("cancel")
                ? "bg-red-100 text-red-800"
                : statusText.includes("check") || statusText.includes("start")
                  ? "bg-blue-100 text-blue-800"
                  : "bg-gray-100 text-gray-700";
          const employee =
            appointmentInfo.employee_details?.name ||
            appointmentInfo.employee_name ||
            boardingInfo.room_details?.room_name;
          const Icon = String(typeLabel).toLowerCase().includes("groom")
            ? Scissors
            : String(typeLabel).toLowerCase().includes("vet")
              ? Syringe
              : Calendar;

          return (
            <div key={service._id || service.ref_id || `${service._orderId}-${index}`} className="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{serviceDate ? formatDate(serviceDate) : "—"}</span>
                    {service.order_number && <span className="text-xs text-gray-500">#{service.order_number}</span>}
                    <span className="text-xs text-gray-400 capitalize">{typeLabel}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Icon className="text-blue-500 w-4 h-4" />
                    <span className="font-medium text-gray-800 text-sm">{serviceName}</span>
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${statusClassName}`}>{status}</span>
              </div>
              <div className="mt-1 text-xs text-gray-600 flex flex-wrap gap-3">
                {endDate && <span>Checkout: {formatDate(endDate)}</span>}
                {employee && <span>{employee}</span>}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "boarding_history":
        return renderBoardingHistory();
      case "overview":
        return renderOverview();
      case "schedule":
        return renderSchedule();
      case "notes":
        return renderNotes();
      case "photos":
        return renderPhotos();
      case "timeline":
        return renderServiceTimeline();
      default:
        return renderBoardingHistory();
    }
  };

  if (!isOpen) return null;
  if (isLoading) return <LoaderSpiner text="Loading pet details..." />;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end">
      <div
        ref={drawerRef}
        className="h-full w-full max-w-[60%] bg-white shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <PetHeader
          petName={headerName}
          petType={headerPetType}
          petBreed={headerBreed}
          petGender={headerGender}
          petAge={String(headerAge || "")}
          petWeight={headerWeight}
          petNeuteredStatus={headerNeuteredStatus}
          petImage={petImage}
          petCoat={headerCoat}
          petTags={headerTags}
          petColor={headerColor}
          petDiet={headerDiet}
          petMealsPerDay={headerMealsPerDay}
          petSize={headerSize}
          petDetails={headerPet}
          medicalConditions={headerMedicalConditions.map((condition) => ({ name: condition }))}
          vaccinations={headerVaccines.map((vaccine) => ({ name: vaccine }))}
          clientName={ownerName}
          clientPhone={ownerMobile}
          onClose={handleClose}
          onImageClick={handleImageClick}
          formatDate={formatDate}
        />
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageUpload}
          accept="image/*"
          className="hidden"
        />

        {/* ========== TABS ========== */}
        <div className="border-b border-gray-200 bg-white">
          <div className="flex">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-3 text-center text-sm font-semibold transition-colors relative flex items-center justify-center gap-1.5 ${
                    activeTab === tab.id
                      ? "text-blue-600 bg-blue-50"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute left-0 right-0 bottom-0 h-1 bg-blue-600" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ========== CONTENT ========== */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          {renderTabContent()}
        </div>

        {/* ========== FOOTER (optional) ========== */}
        <div className="border-t border-gray-200 bg-white p-4">
          <button
            onClick={handleClose}
            className="w-full px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>

      {/* Keep nested drawers if still needed */}
      {showAddAppointmentDrawer && (
        <AddAppointment
          isOpen={showAddAppointmentDrawer}
          onClose={() => setShowAddAppointmentDrawer(false)}
          selectedSlotInfo={null}
          clearSlotSelection={() => {}}
          editMode={false}
          editingAppointment={null}
          onSave={() => setShowAddAppointmentDrawer(false)}
          appointmentStatusRef={null}
          clientDetails={appointmentClientDetails}
          preSelectedPetId={petData?._id}
          isPetDetails={true}
        />
      )}

      {isAddonsDrawerOpen && (
        <SelectAddOnsDrawer
          isOpen={isAddonsDrawerOpen}
          onClose={() => setIsAddonsDrawerOpen(false)}
          onSave={() => setIsAddonsDrawerOpen(false)}
          serviceCategoryId={selectedServiceForAddons?.service_category_type_id}
          petType={selectedServiceForAddons?.pet_type || "Both"}
          selectedAddons={selectedServiceForAddons?.addons || []}
          serviceTypeName={
            selectedServiceForAddons?.service_type_name ||
            selectedServiceForAddons?.service_name
          }
        />
      )}

      {showPetServiceDrawer && (
        <PetServiceCategoryDrawer
          isOpen={showPetServiceDrawer}
          onClose={() => {
            setShowPetServiceDrawer(false);
            setEditingService(null);
          }}
          onUpdate={() => setShowPetServiceDrawer(false)}
          clientId={petData?.vendor_client_id}
          clientName={ownerName}
          clientNumber={ownerMobile}
          itemData={
            editingService || {
              vendor_client_id: petData?.vendor_client_id,
              vendor_pet_id: petData?._id,
              pet_id: petData?._id,
              pet_name: petData?.name,
              pet_type: petData?.pet_type,
              pet_breed: petData?.breed?.breed_name,
              pet_size: petData?.size_details?.size_label,
            }
          }
          orderId={editingService?.order_number || null}
          fromCalendar={false}
          selectedSlotInfo={null}
          editMode={!!editingService}
          onRefreshData={() => {}}
          appointmentStatusRef={null}
          autoOpenPetForm={false}
          isServiceEditMode={!!editingService}
          serviceId={editingService?.service_id}
          vendorServiceId={editingService?.vendor_service_id}
          serviceCategoryTypeId={editingService?.service_category_type_id}
          serviceTypeId={editingService?.service_type_id}
          refId={editingService?.ref_id}
          serviceTypeName={editingService?.service_type_name}
          isPetDetails={true}
        />
      )}
    </div>
  );
};

export default BoardingPetDetailsDrawer;